/****************************************************
 * Module Name:  delayedLotReceiveBarChart.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CommonSnackbar from "../common/commonSnackBar";
import Constants from "../../utility/constants";
import LotIntimationProxy from "../../utility/proxy/manager/manufacturingProxy/lotIntimationProxy";
import LotIntimationModal from "./lotIntimationModal";
import { TbPackage } from "react-icons/tb";
import Utility from "../../utility/utility";

let useColor = Utility.GetRandomColorForBarChart();

const DelayedLotReceiveBarChart = () => {
  const [chartData, setChartData] = useState([]);
  const [responseData, setResponseData] = useState([]);
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [show, setShow] = useState(false);
  const [vendorData, setVendorData] = useState("");

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    handleSearch();
  }, []);

  const handleSearch = () => {
    LotIntimationProxy.GetAllLotIntimation((response) => {
      if (response) {
        setResponseData(response);
        let refRes = JSON.stringify(response);
        let newRes = JSON.parse(refRes).filter(
          (res) => new Date() > new Date(res.expectedreceive)
        );
        let finalResponseWithCombinedValues = newRes.reduce(function (
          accumulator,
          cur
        ) {
          let vendorid = cur.vendorid;
          let found = accumulator.find(function (elem) {
            return elem.vendorid === vendorid;
          });
          if (found) {
            found.weight = Number(found.weight);
            found.weight += parseInt(cur.weight);
          } else {
            accumulator.push(cur);
          }
          return accumulator;
        },
          []);
        setChartData(finalResponseWithCombinedValues);
      } else {
        setErrorSnackbar(true);
      }
    });
  };

  const handleModalOpen = (data) => {
    setShow(true);
    setVendorData(() =>
      responseData.filter((res) => res.vendorid === data.vendorid)
    );
  };

  return (
    <div>
      <CommonSnackbar
        show={errorSnackbar}
        message={Constants.APiFailErrorMessage}
        variant={"error"}
        autoHide={Constants.SnackBarCloseTimer}
        title={"Failed"}
        handleClose={() => setErrorSnackbar(false)}
      />
      <LotIntimationModal
        show={show}
        vendorData={vendorData}
        handleClose={() => setShow(false)}
      />
      <Paper elevation={4}>
        <Card>
          <CardHeader
            avatar={<TbPackage size={28} color={useColor} />}
            title={
              <Typography
                component="span"
                fontWeight={Constants.defaultFontWeight}
                fontSize={"1.25rem"}
              >
                Delayed Lot Receive
              </Typography>
            }
            subheader="Quantity yet to be received post estimated received date by each vendor"
          />
          <CardContent>
            <Box height={matches ? 300 : 500} width={matches ? 320 : 550}>
              <ResponsiveContainer height={matches ? 300 : 500}>
                <BarChart
                  data={chartData}
                  margin={{
                    top: 20,
                    right: 20,
                    bottom: 20,
                    left: 20,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="vendorname" hide={matches} />
                  <YAxis hide={matches} />
                  <Tooltip />
                  <Legend formatter={(value, entry) => value.toUpperCase() + "(CTS)"} />
                  <Bar
                    dataKey="weight"
                    fill={useColor}
                    unit={"cts"}
                    stroke="#000000"
                    strokeWidth={1}
                    label={{ position: "top" }}
                    onClick={(data) => handleModalOpen(data)}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Paper>
    </div>
  );
};

export default DelayedLotReceiveBarChart;
